/**
 * @voltxampere/presets — RC high-pass filter (#3)
 *
 * V1 (DC) → C → out → R → gnd. The cap is in series with the
 * input, so `initialVoltage` is silently ignored (Sprint 6
 * floating-cap limitation; CLAUDE.md). The DC operating point
 * therefore tells the whole story: at DC the cap is open, no
 * current flows, and V(out) = 0 — the high-pass topology blocks
 * DC by construction. Sprint 22+ AC analysis will surface the
 * frequency-dependent passband; today we test the DC blocking
 * invariant.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
// Sprint 25 R2 lane 4D-A — hand-authored layout. Source on the
// left, series capacitor in the middle (DC blocking element),
// shunt resistor rotated 90° on the right. Mirrors RC-LP topology
// but with R/C swapped — the visual rhyme is intentional.
export const rcHighPass = {
    id: "rc-high-pass",
    name: { tr: "RC Yüksek Geçiren Filtre", en: "RC High-Pass Filter" },
    category: "basic",
    difficulty: 2,
    description: {
        tr: "Birinci dereceden yüksek geçiren filtre. C=1µF, R=1kΩ, fc≈159Hz. DC bileşenini bloklar.",
        en: "First-order high-pass filter. C=1µF, R=1kΩ, fc≈159Hz. Blocks DC.",
    },
    circuit: makeCircuit({
        slot: 3,
        name: "RC High-Pass",
        description: "First-order RC high-pass filter — DC blocking",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "in" }),
            makeNet({ name: "out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 1 },
                terminals: { pos: "net_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -160, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "C1",
                type: "capacitor",
                params: { capacitance: 1e-6 },
                terminals: { p1: "net_in", p2: "net_out" },
                label: "C1",
                position: { x: 0, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 1000 },
                terminals: { p1: "net_out", p2: "net_gnd" },
                label: "R1",
                position: { x: 160, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -160, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // in rail: V1.pos (-160, -10) → C1.p1 (-20, 0)
            {
                id: "w_in",
                netId: "net_in",
                path: [
                    { x: -160, y: -10 },
                    { x: -160, y: -40 },
                    { x: -20, y: -40 },
                    { x: -20, y: 0 },
                ],
                style: "orthogonal",
            },
            // out rail: C1.p2 (20, 0) → R1.p1 (160, -20)
            {
                id: "w_out",
                netId: "net_out",
                path: [
                    { x: 20, y: 0 },
                    { x: 160, y: 0 },
                    { x: 160, y: -20 },
                ],
                style: "orthogonal",
            },
            // gnd leg A: V1.neg (-160, 10) → GND.p (-160, 71)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -160, y: 10 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
            // gnd leg B: R1.p2 (160, 20) → GND.p (-160, 71)
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 160, y: 20 },
                    { x: 160, y: 110 },
                    { x: -160, y: 110 },
                    { x: -160, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "out",
            analysis: "dc",
            value: 0,
            tolerance: 0.001,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Yüksek geçiren topoloji DC bileşenini bloklar — kapasitör DC'de açıktır.",
            en: "High-pass topology blocks DC — the capacitor is open at DC.",
        },
        {
            tr: "Köşe frekansı fc = 1/(2πRC) ≈ 159 Hz (Sprint 22+ AC analizinde görselleştirilecek).",
            en: "Corner frequency fc = 1/(2πRC) ≈ 159 Hz (visualised in Sprint 22+ AC analysis).",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 1"],
};
//# sourceMappingURL=rc-high-pass.js.map